/// <amd-module name="Reporting/Common/CacheableQueryService" />
import { ICacheableDataQualityQuery } from "Reporting/Common/ICacheableDataQualityQuery";
import { ICacheableQuery, ITrackCacheHits } from "Reporting/Common/ICacheableQuery";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";

export interface ICacheableQueryService extends IVssService {
    getCacheableQueryResult<T>(query: ICacheableQuery<T>): Promise<T>;
    getDataQualityWarnings<T>(query: ICacheableDataQualityQuery<T>): Promise<string[]>;
    hasCachedEntry<T>(query: ICacheableQuery<T>): boolean;
    resetCache(): void;
}

export const ICacheableQueryServiceName = "ICacheableQueryService";

/**
 * An In-memory implementation for client cache of shared query results.
 */
export class CacheableQueryService extends VssService implements ICacheableQueryService {
    private _cache: { [key: string]: any };
    private _dataQualityCache: { [key: string]: any };
    private _pageContext: IVssPageContext;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._pageContext = pageContext;

        this.resetCache();
    }

    public resetCache(): void {
        this._cache = {};
        this._dataQualityCache = {};
    }

    public getCacheableQueryResult<T>(query: ICacheableQuery<T> | ICacheableDataQualityQuery<T>): Promise<T> {
        const key = query.getKey();
        const cachedResult = this.getEntry<T>(key);

        if (cachedResult) {
            this.notifyCacheHit(query);
            return cachedResult;
        } else {
            if ("runDataQualityAwareQuery" in query) {
                return this.runQueryAndSplitPromises(query).dataPromise;
            } else {
                const resultPromise = query.runQuery(this._pageContext);
                this.setEntry<T>(key, resultPromise);
                return resultPromise;
            }
        }
    }

    /**
     * Method to get any data quality warnings associated with queries obtained using getCacheableQueryResult above.
     * This method also uses the same caching mechanism as getCacheableQueryResult.
     * @param query A query object implementing the ICacheableDataQualityQuery interface.
     */
    public getDataQualityWarnings<T>(query: ICacheableDataQualityQuery<T>): Promise<string[]> {
        const key = query.getKey();
        const cachedResult = this.getDataQualityEntry<T>(key);

        if (cachedResult) {
            this.notifyCacheHit(query);
            return cachedResult;
        } else {
            return this.runQueryAndSplitPromises(query).dataQualityPromise;
        }
    }

    public hasCachedEntry<T>(query: ICacheableQuery<T>): boolean {
        const key = query.getKey();
        return !!this.getEntry<T>(key);
    }

    private runQueryAndSplitPromises<T>(query: ICacheableDataQualityQuery<T>) {
        const key = query.getKey();

        const queryPromise = query.runDataQualityAwareQuery(this._pageContext);

        const dataPromise = queryPromise.then(res => {
            return res.data;
        });

        const dataQualityPromise = queryPromise.then(res => {
            return res.dataQualityWarnings;
        });

        this.setEntry<T>(key, dataPromise);

        this.setDataQualityEntry<T>(key, dataQualityPromise);

        return { dataPromise: dataPromise, dataQualityPromise: dataQualityPromise };
    }

    /** Provides opt-in notification to query implementations supporting cache hit notification. This allows for bookkeeping of redundant requests "the service didn't see".  */
    private notifyCacheHit(query: any): void {
        if (query != null) {
            if ((query as ITrackCacheHits).onCacheHit != null) {
                query.onCacheHit(this._pageContext);
            }
        }
    }

    private getEntry<T>(key: string): Promise<T> {
        return this._cache[key];
    }

    private setEntry<T>(key: string, promise: Promise<T>) {
        this._cache[key] = promise;
    }

    private getDataQualityEntry<T>(key: string): Promise<string[]> {
        return this._dataQualityCache[key];
    }

    private setDataQualityEntry<T>(key: string, promise: Promise<string[]>) {
        this._dataQualityCache[key] = promise;
    }
}

Services.add(ICacheableQueryServiceName, { serviceFactory: CacheableQueryService });
